import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { fileToCompressedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import { useAuth } from "@/contexts/AuthContext";
import BotonCerrar from "@/components/BotonCerrar";
import ClientGuideModal from "@/pages/_clients/ClientGuideModal";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import { PlusIcon, PencilSimpleIcon, EyeIcon, CameraIcon, TrashIcon, FileTextIcon, BuildingsIcon, MapPinIcon, TruckIcon, CheckIcon } from "@phosphor-icons/react";

const REGIONES_FALLBACK = ["Francisco Morazán","Cortés","Atlántida","Choluteca","Olancho","Yoro","Comayagua","Copán","Lempira","Santa Bárbara","Valle","El Paraíso","La Paz","Intibucá","Ocotepeque","Colón","Gracias a Dios","Islas de la Bahía"];
const SEGMENTOS = ["RETAIL","MAYORISTA","CORPORATIVO","AGRICOLA"];

const empty = {
  codigo: "", nombre: "", rtn: "", identidad: "", proyecto: "", region: "Francisco Morazán",
  segmento: "RETAIL", nivel_precio: "p4", vendedor_asignado: "", rtc_asignado_id: "", rtc_asignado_nombre: "",
  direccion: "", telefono: "", email: "", notificar_correo: true,
  limite_credito: 0, dias_credito: 0, dias_bloqueo: 30, exonerado: false, registro_exonerado: "", registro_sag: "",
  referencia1_nombre: "", referencia1_parentesco: "", referencia1_telefono: "",
  referencia2_nombre: "", referencia2_parentesco: "", referencia2_telefono: "",
  notas: "",
};

export default function Clients() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [guia, setGuia] = useState(null);   // guía de remisión del cliente
  const [regiones, setRegiones] = useState(REGIONES_FALLBACK);
  const [reps, setReps] = useState([]);
  const [q, setQ] = usePageState("q", "");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [docs, setDocs] = useState([]);              // Documentos pendientes de subir al crear cliente
  const [shipRefs, setShipRefs] = useState([]);      // Destinos de envío pendientes de crear con el cliente
  const [editing, setEditing] = useState(null);
  // Edición: referencias de envío y documentos YA registrados (se muestran y conservan; los destinos se actualizan)
  const [shipExist, setShipExist] = useState([]);
  const [shipExistOrig, setShipExistOrig] = useState({});
  const [docsExist, setDocsExist] = useState([]);
  const [cargandoRefs, setCargandoRefs] = useState(false);
  const { hasModule, user } = useAuth();
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);   // tarifa del cliente: solo el Administrador General
  // Quien no modifica clientes (p. ej. el RTC) completa referencias de envío y documentos; los datos generales son de consulta
  const puedeDatos = !hasModule || hasModule("clientes", "modificar");
  const soloRefs = Boolean(editing) && !puedeDatos;
  const abrirEdicion = async (c) => {
    setEditing(c); setForm({ ...empty, ...c }); setDocs([]); setShipRefs([]);
    setShipExist([]); setShipExistOrig({}); setDocsExist([]); setCargandoRefs(true); setOpen(true);
    try {
      const [{ data: sd }, { data: dd }] = await Promise.all([
        api.get(`/clients/${c.id}/shipping-destinations`), api.get(`/clients/${c.id}/documents`)]);
      setShipExist(sd || []);
      setShipExistOrig(Object.fromEntries((sd || []).map((x) => [x.id, JSON.stringify(x)])));
      setDocsExist(dd || []);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setCargandoRefs(false); }
  };
  // CLI-03 — Quick subcuenta modal
  const [projModal, setProjModal] = useState(null);  // {cliente_id, cliente_nombre}
  const [projForm, setProjForm] = useState({ codigo: "", nombre: "", descripcion: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: r }] = await Promise.all([
        api.get("/clients"),
        api.get("/representatives"),
      ]);
      setItems(data); setReps(r);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const busqueda = useLiveSearch(items, (c) => [c.codigo_cliente, c.codigo, c.nombre, c.rtn, c.identidad, c.region, c.email, c.telefono,
    c.rtc_asignado_nombre, c.vendedor_asignado], { estado: [q, setQ] });
  useEffect(() => {
    api.get("/regions", { params: { activo: true } })
      .then(({ data }) => { if (data?.length) setRegiones(data.map((r) => r.codigo)); })
      .catch(() => {});
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const rtc = reps.find((r) => r.id === form.rtc_asignado_id);
      const payload = {
        ...form,
        limite_credito: Number(form.limite_credito),
        dias_credito: Number(form.dias_credito),
        dias_bloqueo: Number(form.dias_bloqueo || 30),
        rtc_asignado_nombre: rtc ? rtc.nombre : "",
      };
      let cliente_id;
      for (const x of [...shipRefs, ...shipExist]) {
        if ((x.alias || x.direccion) && (!x.alias?.trim() || !x.direccion?.trim())) {
          toast.error("Cada referencia de envío necesita alias y dirección completa"); return false;
        }
      }
      if (editing) {
        if (puedeDatos) {
          await api.put(`/clients/${editing.id}`, payload);
          toast.success("Cliente actualizado");
        }
        cliente_id = editing.id;
        // referencias de envío existentes que se modificaron
        const cambiadas = shipExist.filter((x) => shipExistOrig[x.id] !== JSON.stringify(x));
        for (const x of cambiadas) {
          await api.put(`/clients/${cliente_id}/shipping-destinations/${x.id}`, x);
          setShipExistOrig((o) => ({ ...o, [x.id]: JSON.stringify(x) }));
        }
        if (cambiadas.length) toast.success(`${cambiadas.length} referencia(s) de envío actualizada(s)`);
      } else {
        const { data } = await api.post("/clients", payload);
        cliente_id = data.id;
        toast.success("Cliente creado");
      }
      // Documentos nuevos: cada uno guardado sale de la lista (si otro falla, al reintentar no se duplica)
      if (!editing) setEditing({ id: cliente_id });     // un reintento ya no vuelve a crear el cliente
      let okCount = 0;
      for (const d of [...docs]) {
        await api.post(`/clients/${cliente_id}/documents`, {
          cliente_id, tipo: d.tipo, nombre: d.nombre, url: d.url,
          fecha_emision: d.fecha_emision || null, fecha_vencimiento: d.fecha_vencimiento || null, notas: d.notas || "",
        });
        okCount++;
        setDocs((l) => l.filter((x) => x !== d));
      }
      if (okCount > 0) toast.success(`${okCount} documento(s) cargado(s)`);
      // Referencias de envío nuevas
      let okShip = 0;
      for (const sr of [...shipRefs]) {
        if (!sr.alias?.trim() || !sr.direccion?.trim()) continue;
        await api.post(`/clients/${cliente_id}/shipping-destinations`, sr);
        okShip++;
        setShipRefs((l) => l.filter((x) => x !== sr));
      }
      if (okShip > 0) toast.success(`${okShip} destino(s) de envío guardado(s)`);
      setOpen(false); setForm(empty); setDocs([]); setShipRefs([]); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); return false; }
  };
  const cm = useModalForm({ value: { form, docs, shipRefs, shipExist }, abierto: open, listo: !cargandoRefs,
    onClose: () => setOpen(false), onSave });
  const guardarSubcuenta = async () => {
    if (!projForm.nombre.trim()) { toast.error("Indica el nombre de la subcuenta"); return; }
    try {
      const { data } = await api.post(`/clients/${projModal.cliente_id}/projects`, { ...projForm, cliente_id: projModal.cliente_id });
      toast.success(`Subcuenta ${data.codigo_subcuenta} creada`);
      setProjModal(null);
    } catch (er) { toast.error(apiErr(er)); }
  };
  const sm = useModalForm({ value: projForm, abierto: Boolean(projModal), onClose: () => setProjModal(null), onSave: guardarSubcuenta });

  const addDocFile = async (tipo, file) => {
    if (!file) return;
    try {
      const url = await fileToCompressedDataUrl(file);
      setDocs((d) => [...d, { tipo, nombre: file.name, url, fecha_emision: "", fecha_vencimiento: "", notas: "" }]);
      toast.success(`${file.name} agregado`);
    } catch (er) { toast.error(er.message || "Error procesando archivo"); }
  };

  return (
    <Layout
      title="Clientes"
      subtitle={`${items.length} clientes · Segmentación, créditos y estado de cuenta`}
      actions={
        <button onClick={() => { setEditing(null); setForm(empty); setDocs([]); setShipRefs([]); setOpen(true); }} data-testid="new-client-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
          <PlusIcon size={16} weight="bold" /> Nuevo cliente
        </button>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex gap-3">
          <SearchBox busqueda={busqueda} placeholder="Buscar por nombre, RTN, código, identidad, región o RTC…" testid="clients-search" label="Buscar clientes" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Código</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">RTN</th>
                <th className="p-3">Región</th>
                <th className="p-3">RTC</th>
                <th className="p-3">Nivel</th>
                <th className="p-3 text-right">Límite crédito</th>
                <th className="p-3">Última compra</th>
                <th className="p-3 text-right pr-5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : busqueda.resultados.length === 0 ? <tr><td colSpan="9" className="p-8 text-center text-text-muted" data-testid="clients-sin-resultados">{busqueda.activa ? `Sin clientes que coincidan con «${busqueda.texto}».` : "Aún no hay clientes."}</td></tr>
              : busqueda.resultados.map((c) => (
                <tr key={c.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`client-row-${c.codigo}`}>
                  <td className="p-3 pl-5" data-testid={`client-codigo-${c.codigo_cliente || c.id}`}>
                    <div className="font-mono text-xs text-agro-green font-semibold">{c.codigo_cliente || "—"}</div>
                    {c.codigo && c.codigo !== c.codigo_cliente && <div className="text-[10px] text-text-muted truncate max-w-[220px]" title="Referencia anterior (sistema de origen)">{c.codigo}</div>}
                  </td>
                  <td className="p-3">
                    <div className="text-sm text-text-primary flex items-center gap-2">
                      {c.nombre}
                      {c.exonerado && <span className="text-[10px] px-1.5 py-0.5 bg-agro-yellow/20 text-agro-yellow rounded">EXONERADO</span>}
                      {c.credito_autorizado_hasta && <span title="Crédito autorizado temporalmente" className="text-[10px] px-1.5 py-0.5 bg-agro-green/20 text-agro-green rounded inline-flex items-center gap-0.5"><CheckIcon size={10} weight="bold" aria-hidden="true" /> Crédito autorizado</span>}
                    </div>
                    <div className="text-[11px] text-text-muted">{c.email || "—"} · {c.telefono || "sin teléfono"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs text-text-secondary">{c.rtn || "—"}</td>
                  <td className="p-3 text-xs text-text-secondary">{c.region}</td>
                  <td className="p-3 text-xs text-text-secondary">{c.rtc_asignado_nombre || "—"}</td>
                  <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded font-medium uppercase ${
                    c.nivel_precio === "p0" ? "bg-agro-yellow/20 text-agro-yellow" :
                    c.nivel_precio === "p1" ? "bg-app border border-border text-text-secondary" :
                    "bg-agro-green/10 text-agro-green"
                  }`}>{c.nivel_precio}</span></td>
                  <td className="p-3 text-right text-sm text-text-primary">{fmtL(c.limite_credito)}</td>
                  <td className="p-3 text-xs text-text-secondary">{c.ultima_compra ? fmtDate(c.ultima_compra) : "—"}</td>
                  <td className="p-3 pr-5 text-right">
                    <button onClick={() => navigate(`/clientes/${c.id}`)} title="Ver detalle / estado de cuenta" className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`view-client-${c.codigo}`}><EyeIcon size={16} /></button>
                    <button onClick={() => setGuia(c)} title="Imprimir guía de remisión" className="p-1.5 text-text-muted hover:text-blue-400" data-testid={`guide-client-${c.codigo}`}><TruckIcon size={16} /></button>
                    <button onClick={() => { setProjModal({ cliente_id: c.id, cliente_nombre: c.nombre, codigo_cliente: c.codigo_cliente }); setProjForm({ codigo: "", nombre: "", descripcion: "" }); }}
                      title="Nueva subcuenta / proyecto" className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`new-proj-${c.codigo}`}><BuildingsIcon size={16} /></button>
                    <button onClick={() => abrirEdicion(c)} title={puedeDatos ? "Editar cliente" : "Completar referencias de envío y documentos"}
                      className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`edit-client-${c.codigo}`}><PencilSimpleIcon size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {guia && <ClientGuideModal cliente={guia} onClose={() => setGuia(null)} />}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...cm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar cliente" : "Nuevo cliente"}</h3>
              <BotonCerrar onClick={cm.cerrar} />
            </div>
            <form onSubmit={cm.guardar} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {soloRefs && (
                <div className="md:col-span-2 text-xs px-3 py-2 rounded-md border border-blue-500/30 bg-blue-500/5 text-blue-300" data-testid="solo-referencias">
                  Puedes agregar o actualizar las referencias de envío y cargar documentos. Los datos generales del cliente los modifica el Administrador o el Delegado.
                </div>
              )}
              <fieldset disabled={soloRefs} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0 disabled:opacity-60">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary block mb-1.5">Código de cliente</label>
                <div className="w-full bg-app/50 border border-border rounded-md px-3 py-2 text-sm font-mono text-agro-green" data-testid="client-codigo-auto">
                  {editing?.codigo_cliente || "Se asigna automáticamente al guardar"}
                </div>
              </div>
              <F label="Referencia anterior (opcional)" v={form.codigo} on={(v) => setForm({...form, codigo:v})} testid="client-codigo" />
              <F label="Nombre / razón social *" v={form.nombre} on={(v) => setForm({...form, nombre:v})} required testid="client-nombre" />
              <F label="RTN" v={form.rtn} on={(v) => setForm({...form, rtn:v})} />
              <F label="Identidad" v={form.identidad} on={(v) => setForm({...form, identidad:v})} />
              <Sel label="Región" v={form.region} on={(v) => setForm({...form, region:v})} opts={regiones} />
              <Sel label="Segmento" v={form.segmento} on={(v) => setForm({...form, segmento:v})} opts={SEGMENTOS} />
              <Sel label={esAdmin ? "Nivel de precio" : "Nivel de precio (lo asigna el Administrador General)"} disabled={!esAdmin}
                v={form.nivel_precio} on={(v) => setForm({...form, nivel_precio:v})} opts={[
                {v: "p0", l: "P0"},
                {v: "p1", l: "P1"},
                {v: "p2", l: "P2"},
                {v: "p3", l: "P3"},
                {v: "p4", l: "P4"},
              ]} />
              <F label="Teléfono" v={form.telefono} on={(v) => setForm({...form, telefono:v})} />
              <F label="Email" v={form.email} on={(v) => setForm({...form, email:v})} />
              <F label="Dirección" v={form.direccion} on={(v) => setForm({...form, direccion:v})} />
              <F label="Vendedor asignado" v={form.vendedor_asignado} on={(v) => setForm({...form, vendedor_asignado:v})} />
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">RTC asignado</label>
                <select data-testid="client-rtc" value={form.rtc_asignado_id ?? ""} onChange={(e) => setForm({...form, rtc_asignado_id: e.target.value})}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                  <option value="">{reps.length ? "— Sin RTC —" : "— No hay RTC registrados en RRHH —"}</option>
                  {form.rtc_asignado_id && !reps.some((r) => r.id === form.rtc_asignado_id) && (
                    <option value={form.rtc_asignado_id}>{form.rtc_asignado_nombre || "RTC"} (no disponible)</option>)}
                  {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
                </select>
              </div>
              <F label="Proyecto" v={form.proyecto} on={(v) => setForm({...form, proyecto:v})} />
              <F label="Límite crédito (L.)" type="number" v={form.limite_credito} on={(v) => setForm({...form, limite_credito:v})} />
              <F label="Días crédito" type="number" v={form.dias_credito} on={(v) => setForm({...form, dias_credito:v})} />
              <F label="Días para bloqueo (vencidos)" type="number" v={form.dias_bloqueo} on={(v) => setForm({...form, dias_bloqueo:v})} testid="client-dias-bloqueo" />
              <div className="flex items-center gap-2 mt-6">
                <input id="notif" type="checkbox" checked={form.notificar_correo} onChange={(e) => setForm({...form, notificar_correo:e.target.checked})} className="w-4 h-4 accent-agro-green" data-testid="client-notificar" />
                <label htmlFor="notif" className="text-sm text-text-primary">Notificar estado de cuenta por correo</label>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input id="exo" type="checkbox" checked={form.exonerado} onChange={(e) => setForm({...form, exonerado:e.target.checked})} className="w-4 h-4 accent-agro-green" />
                <label htmlFor="exo" className="text-sm text-text-primary">Cliente exonerado</label>
              </div>
              {form.exonerado && <>
                <F label="Registro exonerado" v={form.registro_exonerado} on={(v) => setForm({...form, registro_exonerado:v})} />
                <F label="Registro SAG" v={form.registro_sag} on={(v) => setForm({...form, registro_sag:v})} />
              </>}

              <div className="md:col-span-2 mt-2 pt-4 border-t border-border/60">
                <h4 className="font-heading text-sm font-semibold text-agro-green uppercase tracking-wider mb-1">Referencias personales</h4>
                <p className="text-[11px] text-text-muted">Personas que avalan al cliente (familiares, conocidos, contactos comerciales).</p>
              </div>
              <F label="Referencia 1 · Nombre" v={form.referencia1_nombre} on={(v) => setForm({...form, referencia1_nombre:v})} testid="client-ref1-nombre" />
              <F label="Referencia 1 · Parentesco / relación" v={form.referencia1_parentesco} on={(v) => setForm({...form, referencia1_parentesco:v})} testid="client-ref1-parentesco" />
              <F label="Referencia 1 · Teléfono" v={form.referencia1_telefono} on={(v) => setForm({...form, referencia1_telefono:v})} testid="client-ref1-telefono" />
              <div />
              <F label="Referencia 2 · Nombre" v={form.referencia2_nombre} on={(v) => setForm({...form, referencia2_nombre:v})} testid="client-ref2-nombre" />
              <F label="Referencia 2 · Parentesco / relación" v={form.referencia2_parentesco} on={(v) => setForm({...form, referencia2_parentesco:v})} testid="client-ref2-parentesco" />
              <F label="Referencia 2 · Teléfono" v={form.referencia2_telefono} on={(v) => setForm({...form, referencia2_telefono:v})} testid="client-ref2-telefono" />
              <div />

              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Notas internas</label>
                <textarea data-testid="client-notas" value={form.notas ?? ""} onChange={(e) => setForm({...form, notas:e.target.value})} rows={2}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green resize-none" />
              </div>

              </fieldset>
              {(
                <div className="md:col-span-2 pt-4 mt-2 border-t border-border/60" data-testid="seccion-referencias-envio">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-heading text-sm font-semibold text-blue-400 uppercase tracking-wider flex items-center gap-2">
                      <TruckIcon size={16} /> Referencias de envío
                    </h4>
                    <button type="button"
                      onClick={() => {
                        if (shipExist.length + shipRefs.length >= 5) { toast.error("Máximo 5 destinos por cliente"); return; }
                        setShipRefs([...shipRefs, { alias: "", direccion: "", encargado: "", telefono: "", referencia: "", ciudad: "", departamento: "" }]);
                      }}
                      data-testid="add-ship-ref-btn"
                      className="text-xs px-3 py-1 border border-blue-500/40 text-blue-400 rounded-md hover:bg-blue-500/10 flex items-center gap-1">
                      <PlusIcon size={12} weight="bold" /> Agregar dirección
                    </button>
                  </div>
                  <p className="text-[11px] text-text-muted mb-3">
                    Direcciones alternativas a donde enviar los pedidos de este cliente (fincas, sucursales, bodegas). Máximo 5.
                  </p>

                  {cargandoRefs && <div className="text-xs text-text-muted mb-2">Cargando referencias registradas…</div>}
                  {shipExist.length > 0 && (
                    <div className="space-y-3 mb-3" data-testid="ship-exist-list">
                      {shipExist.map((s, i) => {
                        const upd = (k, v) => setShipExist(shipExist.map((x, xi) => xi === i ? { ...x, [k]: v } : x));
                        return (
                          <div key={s.id} className="bg-app/40 border border-blue-500/30 rounded-md p-3" data-testid={`ship-exist-${i}`}>
                            <div className="text-xs font-semibold text-blue-400 flex items-center gap-1 mb-2">
                              <MapPinIcon size={12} weight="fill" /> Registrado · {s.alias}
                              {shipExistOrig[s.id] !== JSON.stringify(s) && <span className="ml-2 text-agro-yellow font-normal">(modificado)</span>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <input value={s.alias || ""} onChange={(e) => upd("alias", e.target.value)} placeholder="Alias *" data-testid={`ship-exist-alias-${i}`}
                                className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                              <input value={s.encargado || ""} onChange={(e) => upd("encargado", e.target.value)} placeholder="Encargado / persona a recibir"
                                data-testid={`ship-exist-encargado-${i}`} className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                              <input value={s.direccion || ""} onChange={(e) => upd("direccion", e.target.value)} placeholder="Dirección completa *"
                                data-testid={`ship-exist-direccion-${i}`} className="md:col-span-2 bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                              <input value={s.ciudad || ""} onChange={(e) => upd("ciudad", e.target.value)} placeholder="Ciudad"
                                className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                              <input value={s.telefono || ""} onChange={(e) => upd("telefono", e.target.value)} placeholder="Teléfono contacto en destino"
                                data-testid={`ship-exist-telefono-${i}`} className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                              <input value={s.referencia || ""} onChange={(e) => upd("referencia", e.target.value)} placeholder="Referencia (ej: km 15 carretera al norte)"
                                className="md:col-span-2 bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {shipRefs.length === 0 ? (shipExist.length > 0 ? null :
                    <div className="border border-dashed border-border/60 rounded-md p-4 text-center text-xs text-text-muted">
                      Sin destinos de envío alternativos. Click en "Agregar dirección" para agregar el primero.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {shipRefs.map((s, i) => (
                        <div key={i} className="bg-app/40 border border-blue-500/20 rounded-md p-3" data-testid={`ship-ref-${i}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-blue-400 flex items-center gap-1">
                              <MapPinIcon size={12} weight="fill" /> Nuevo destino #{shipExist.length + i + 1}
                            </span>
                            <button aria-label="Eliminar" type="button" onClick={() => setShipRefs(shipRefs.filter((_, x) => x !== i))}
                              className="text-text-muted hover:text-red-400"
                              data-testid={`ship-ref-del-${i}`}>
                              <TrashIcon size={14} />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input value={s.alias} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, alias: e.target.value } : x))}
                              placeholder="Alias (ej: Finca Los Pinos)"
                              data-testid={`ship-ref-alias-${i}`}
                              className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            <input value={s.encargado} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, encargado: e.target.value } : x))}
                              placeholder="Encargado / persona a recibir"
                              className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            <input value={s.direccion} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, direccion: e.target.value } : x))}
                              placeholder="Dirección completa *"
                              data-testid={`ship-ref-direccion-${i}`}
                              className="md:col-span-2 bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            <input value={s.ciudad} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, ciudad: e.target.value } : x))}
                              placeholder="Ciudad"
                              className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            <input value={s.telefono} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, telefono: e.target.value } : x))}
                              placeholder="Teléfono contacto en destino"
                              className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                            <input value={s.referencia} onChange={(e) => setShipRefs(shipRefs.map((x, xi) => xi === i ? { ...x, referencia: e.target.value } : x))}
                              placeholder="Referencia (ej: km 15 carretera al norte)"
                              className="md:col-span-2 bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-blue-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(
                <div className="md:col-span-2 pt-4 mt-2 border-t border-border/60" data-testid="seccion-documentos">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-heading text-sm font-semibold text-agro-green uppercase tracking-wider flex items-center gap-2">
                      <CameraIcon size={16} /> Documentos del cliente (fotos)
                    </h4>
                    <span className="text-[11px] text-text-muted">{docsExist.length ? `${docsExist.length} registrado(s) · ` : ""}{docs.length} nuevo(s)</span>
                  </div>
                  {docsExist.length > 0 && (
                    <div className="space-y-2 mb-3" data-testid="docs-exist-list">
                      {docsExist.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 bg-app/40 border border-agro-green/20 rounded p-2">
                          {(d.url || "").startsWith("data:image/") ? (
                            <img src={d.url} alt={d.nombre} className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-card flex items-center justify-center rounded"><FileTextIcon size={20} className="text-text-muted" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-text-primary truncate">{d.nombre}</div>
                            <div className="text-[10px] text-text-muted uppercase">{(d.tipo || "").replace("_", " ")} · registrado {fmtDate(d.created_at)}</div>
                          </div>
                          <a href={d.url} target="_blank" rel="noreferrer" download={d.nombre} className="text-[11px] text-agro-green hover:underline">Ver</a>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-text-muted mb-3">Adjunta fotos de ID/licencia, RTN, escrituras, letras de cambio, cuenta bancaria, etc. Se comprimen automáticamente.</p>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    {[
                      {tipo: "id", label: "Identidad / Licencia", testid: "doc-upload-id"},
                      {tipo: "rtn", label: "RTN", testid: "doc-upload-rtn"},
                      {tipo: "escritura", label: "Escritura", testid: "doc-upload-escritura"},
                      {tipo: "letra_cambio", label: "Letra de cambio", testid: "doc-upload-letra"},
                      {tipo: "cuenta_bancaria", label: "Cuenta bancaria", testid: "doc-upload-banco"},
                      {tipo: "otro", label: "Otro", testid: "doc-upload-otro"},
                    ].map((b) => (
                      <label key={b.tipo} data-testid={b.testid} className="border border-dashed border-border hover:border-agro-green/60 hover:bg-agro-green/5 rounded-md p-2 text-xs text-center cursor-pointer transition-colors flex flex-col items-center gap-1">
                        <CameraIcon size={18} className="text-agro-green" />
                        <span className="text-text-secondary">{b.label}</span>
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { addDocFile(b.tipo, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                    ))}
                  </div>

                  {docs.length > 0 && (
                    <div className="space-y-2" data-testid="docs-pending-list">
                      {docs.map((d, i) => (
                        <div key={i} className="flex items-center gap-3 bg-app border border-border/40 rounded p-2">
                          {d.url.startsWith("data:image/") ? (
                            <img src={d.url} alt={d.nombre} className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-card flex items-center justify-center rounded"><FileTextIcon size={20} className="text-text-muted" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-text-primary truncate">{d.nombre}</div>
                            <div className="text-[10px] text-text-muted uppercase">{d.tipo.replace("_", " ")} · {Math.round(d.url.length / 1024)} KB</div>
                          </div>
                          <button aria-label="Eliminar" type="button" onClick={() => setDocs(docs.filter((_, x) => x !== i))} className="text-text-muted hover:text-red-400 p-1"><TrashIcon size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={cm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button type="submit" data-testid="client-save-btn" disabled={cm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover disabled:opacity-40">{cm.saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CLI-03 — Modal rápido "Nueva subcuenta" desde el listado */}
      {projModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...sm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Nueva subcuenta</div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><BuildingsIcon size={20} className="text-agro-green" /> {projModal.cliente_nombre}</h3>
              </div>
              <BotonCerrar onClick={sm.cerrar} />
            </div>
            <form onSubmit={sm.guardar} className="p-5 space-y-3" data-testid="quick-proj-form">
              <div>
                <div className="text-xs text-text-secondary bg-app border border-border rounded-md px-3 py-2 mb-2" data-testid="quick-proj-codigo-auto">
                  Código de subcuenta: <b className="font-mono text-agro-green">{projModal.codigo_cliente ? `${projModal.codigo_cliente}-###` : "automático"}</b> · se asigna al guardar (siguiente de la secuencia del cliente).
                </div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Código corto (opcional)</label>
                <input value={projForm.codigo} onChange={(e) => setProjForm({ ...projForm, codigo: e.target.value })}
                  placeholder="P. ej. FINCA-NORTE" data-testid="quick-proj-codigo"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Nombre *</label>
                <input value={projForm.nombre} onChange={(e) => setProjForm({ ...projForm, nombre: e.target.value })}
                  placeholder="Finca Los Pinos / Ciclo 2026A" data-testid="quick-proj-nombre"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Descripción</label>
                <textarea value={projForm.descripcion} onChange={(e) => setProjForm({ ...projForm, descripcion: e.target.value })}
                  rows="2" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={sm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button type="submit" data-testid="quick-proj-save" disabled={sm.saving || !projForm.nombre.trim()} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover disabled:opacity-40">{sm.saving ? "Guardando…" : "Crear subcuenta"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function F({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}
function Sel({ label, v, on, opts, disabled = false }) {
  // Soporta opts como ["RETAIL", "MAYORISTA"] o como [{v: "p0", l: "P0 — Especial"}]
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <select value={v ?? ""} disabled={disabled} onChange={(e) => on(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green disabled:opacity-60">
        {opts.map((o) => {
          if (typeof o === "string") return <option key={o} value={o}>{o}</option>;
          return <option key={o.v} value={o.v}>{o.l}</option>;
        })}
      </select>
    </div>
  );
}
